import { Button } from "@/components/ui/button";
import { BarChart3, MessageSquare, TrendingUp, FileText, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { TrafficBadge } from "@/components/TrafficBadge";
import { DeltaChip } from "@/components/DeltaChip";
import { SiteNav } from "@/components/landing/SiteNav";
import { SiteFooter } from "@/components/landing/SiteFooter";

const steps = [
  {
    icon: BarChart3,
    title: "Define your KPIs",
    description:
      "Pick 3–9 metrics that matter. Set target bands (min/max) and assign an owner per KPI. Optionally sync from Google Sheets, QuickBooks, or Stripe.",
  },
  {
    icon: MessageSquare,
    title: "Get WhatsApp reminders",
    description:
      "48h, 12h, and 2h before your weekly deadline, owners receive a WhatsApp message with quick-submit buttons. Takes 20 seconds from your phone.",
  },
  {
    icon: TrendingUp,
    title: "Submit with context",
    description:
      "Enter the value. If it's outside your band or delta is high, you must add a one-line cause and plan. Evidence links optional.",
  },
  {
    icon: FileText,
    title: "Everyone gets the summary",
    description:
      "Board, team, and custom recipients receive a concise WhatsApp summary: traffic lights, arrows (↑↓→), top 3 focus areas. PDF available for download.",
  },
];

const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteNav />

      <section className="container mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-12 sm:pb-20 flex-1">
        <div className="text-center space-y-4 mb-12 sm:mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight">
            How Memo works
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            A simple weekly cadence that keeps everyone aligned. No manual
            updates, no last-minute scrambling before board calls.
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-10 sm:space-y-16">
          {steps.map((step, i) => (
            <div
              key={i}
              className="flex flex-col sm:flex-row gap-4 sm:gap-8 items-start"
            >
              <div className="flex-shrink-0">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <step.icon className="h-6 w-6 text-primary" />
                </div>
              </div>
              <div className="space-y-2 flex-1">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-muted-foreground">
                    Step {i + 1}
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Example summary preview */}
        <div className="mt-16 sm:mt-20 max-w-2xl mx-auto">
          <div className="bg-card border border-border rounded-xl p-6 sm:p-8 space-y-6">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Example weekly summary</h3>
              <p className="text-sm text-muted-foreground">
                Week of Jan 6, 2025 • BWE Studio Demo
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <TrafficBadge status="green" />
                  <span className="font-medium tabular-nums text-sm sm:text-base">
                    MRR: ₹12.5L
                  </span>
                </div>
                <DeltaChip delta={15} unit="%" size="sm" />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <TrafficBadge status="amber" />
                  <span className="font-medium tabular-nums text-sm sm:text-base">
                    Churn: 3.8%
                  </span>
                </div>
                <DeltaChip delta={-0.5} unit="%" size="sm" />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <TrafficBadge status="red" />
                  <span className="font-medium tabular-nums text-sm sm:text-base">
                    CAC payback: 8 mo
                  </span>
                </div>
                <DeltaChip delta={2} unit=" mo" size="sm" />
              </div>
            </div>

            <div className="pt-4 border-t border-border space-y-2">
              <p className="text-sm font-medium">Top 3 focus areas:</p>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>
                  CAC payback up due to increased ad spend; testing new channels
                </li>
                <li>
                  Churn slightly elevated; implementing onboarding improvements
                </li>
                <li>
                  MRR growth strong; 3 enterprise deals closing this month
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 sm:mt-16 text-center">
          <Link to="/auth">
            <Button size="lg" className="gap-2 w-full sm:w-auto">
              Get started with Memo <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default HowItWorks;
